// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type NodeProps,
  ResponsiveTreeMap,
  svgLabelTransform,
  svgNodeTransform,
} from '@nivo/treemap';
import { ChartContainer } from '@shared/ui-kit/components';
import { useCallback, useMemo, type FC } from 'react';
import { BasicTooltip } from '@nivo/tooltip';
import { memo } from 'react';
import { animated, to } from '@react-spring/web';
import { useTheme } from '@nivo/core';
import { type ReturnsHeatMapValue } from '@shared/data-access/interfaces/common/returns';
import { formatToPercent } from '@shared/ui-kit/utils';
import { useTranslation } from 'react-i18next';

export interface ReturnsHeatMapProps {
  readonly data: ReturnsHeatMapValue[];
}

/* istanbul ignore next */
const NonMemoizedTreeMapNode = ({
  node,
  animatedProps,
  borderWidth,
  enableLabel,
  labelSkipSize,
}: NodeProps<Partial<ReturnsHeatMapValue> & { color: string }>) => {
  const theme = useTheme();

  const showLabel =
    enableLabel &&
    node.isLeaf &&
    (labelSkipSize === 0 || Math.min(node.width, node.height) > labelSkipSize);

  return (
    <animated.g transform={svgNodeTransform(animatedProps.x, animatedProps.y)}>
      <animated.rect
        data-testid={`node.${node.id}`}
        width={to(animatedProps.width, v => Math.max(v, 0))}
        height={to(animatedProps.height, v => Math.max(v, 0))}
        fill={node.fill ? node.fill : animatedProps.color}
        strokeWidth={borderWidth}
        stroke={node.borderColor}
        fillOpacity={node.opacity}
        onMouseEnter={node.onMouseEnter}
        onMouseMove={node.onMouseMove}
        onMouseLeave={node.onMouseLeave}
        onClick={node.onClick}
      />
      {showLabel && (
        <animated.text
          data-testid={`label.${node.id}`}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            ...theme.labels.text,
            fill: node.labelTextColor,
            pointerEvents: 'none',
          }}
          fillOpacity={animatedProps.labelOpacity}
          transform={svgLabelTransform(
            animatedProps.labelX,
            animatedProps.labelY,
            animatedProps.labelRotation,
          )}
        >
          <tspan
            x="0"
            className="overflow-hidden text-ellipsis whitespace-nowrap"
            width={100}
          >
            {node.label}
          </tspan>
          <tspan x="0" dy={theme.labels.text.fontSize} className="money-display">
            {formatToPercent(node.data.value!, 1)}
          </tspan>
        </animated.text>
      )}
    </animated.g>
  );
};

/* istanbul ignore next */
export const TreeMapNode = memo(
  NonMemoizedTreeMapNode,
) as typeof NonMemoizedTreeMapNode;

export const ReturnsHeatMap: FC<ReturnsHeatMapProps> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';

  const getColor = useCallback((value: number) => {
    const colors = [
      [-3, '#9f1f29'],
      [-2, '#f33649'],
      [-1, '#f77c80'],
      [0, '#c2c6cd'],
      [1, '#42bb7f'],
      [2, '#08a060'],
      [3, '#05803a'],
    ] as const;

    const findClosestColor = (targetValue: number) => {
      return colors.reduce((minPair, currentPair) => {
        const diff = Math.abs(currentPair[0] - targetValue);
        const minDiff = Math.abs(minPair[0] - targetValue);

        if (diff < minDiff) {
          return currentPair;
        }

        return minPair;
      }, colors[0])[1];
    };

    const closestColor = findClosestColor(value);

    return closestColor;
  }, []);

  const returns: Partial<ReturnsHeatMapValue> & { color: string } = useMemo(
    () => ({
      id: 'returns',
      name: 'returns',
      color: 'var(--highlightBlue)',
      children: data.map(value => ({
        ...value,
        color: getColor(value.value * 100),
      })),
    }),
    [data, getColor],
  );

  const hasData = useMemo(() => data.length > 0, [data.length]);

  return (
    <ChartContainer
      header={t('glossary.heat-map', { ns: 'glossary' })}
      className="[&_.chart-container]:!h-[490px]"
      data-testid="returns-heatmap"
    >
      {hasData ? (
        <ResponsiveTreeMap
          data={returns}
          identity="id"
          value="weight"
          valueFormat=" >-.2%"
          colors={({ data }) => data.color}
          margin={{ top: 20, right: 10, bottom: 10, left: 10 }}
          label={({ data }) => data.ticker!}
          labelSkipSize={100}
          nodeComponent={TreeMapNode}
          labelTextColor="var(--white)"
          orientLabel={false}
          nodeOpacity={1}
          enableParentLabel={false}
          innerPadding={4}
          tile="binary"
          borderWidth={0}
          animate
          borderColor="var(--highlightBlue)"
          theme={{
            labels: {
              text: {
                fontSize: 24,
                fontFamily: 'poppins',
                lineHeight: 35,
                textAlign: 'center',
              },
            },
          }}
          tooltip={({ node }) => (
            <BasicTooltip
              id={<span className="uppercase">{node.data.name}</span>}
              enableChip
              color={node.color}
              value={node.data.value}
              format={value =>
                new Intl.NumberFormat(locale, {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  trailingZeroDisplay: 'stripIfInteger',
                  useGrouping: true,
                }).format(value as number)
              }
            />
          )}
        />
      ) : (
        <div
          className="mx-auto mt-5 space-y-4 px-8 text-center font-semibold text-border-dark"
          data-testid="no-data-message"
        >
          <p>No data available</p>
        </div>
      )}
    </ChartContainer>
  );
};
